import { useLayoutEffect, useMemo } from "react";

import { useUser } from "@/auth/UserContext";
import { formatDimensionValue } from "@/lib/formatDimensionValue";
import { formatPersonName } from "@/lib/formatPersonName";
import type { FeedEntityScope } from "@/types/feed";

import { slugifyPartyKey } from "./feedEntityAliases";

/** Linked portfolio identities for the signed-in user (mirrors API feed scope). */
export interface FeedViewerPartyContext {
  salesPersonLink?: string | null;
  openedByValues?: string[];
  agentValues?: string[];
}

const EMPTY_CTX: FeedViewerPartyContext = {
  salesPersonLink: null,
  openedByValues: [],
  agentValues: [],
};

let liveCtx: FeedViewerPartyContext | null = null;

export function setLiveFeedViewerParty(ctx: FeedViewerPartyContext | null) {
  liveCtx = ctx;
}

export function liveFeedViewerParty(): FeedViewerPartyContext | null {
  return liveCtx;
}

export function resolveFeedViewerPartyContext(input: {
  salesPersonLink?: string | null;
  openedByValues?: string[] | null;
  agentValues?: string[] | null;
}): FeedViewerPartyContext {
  return {
    salesPersonLink: input.salesPersonLink?.trim() || null,
    openedByValues: (input.openedByValues ?? [])
      .map((v) => v.trim())
      .filter(Boolean),
    agentValues: (input.agentValues ?? [])
      .map((v) => v.trim())
      .filter(Boolean),
  };
}

export function hasFeedViewerPartyLink(ctx: FeedViewerPartyContext): boolean {
  return Boolean(
    ctx.salesPersonLink?.trim() ||
      (ctx.openedByValues?.length ?? 0) > 0 ||
      (ctx.agentValues?.length ?? 0) > 0,
  );
}

function normalizePartyToken(value: string): string {
  return value.trim().toLowerCase();
}

function partyTokensEqual(a: string, b: string): boolean {
  if (!a.trim() || !b.trim()) return false;
  if (normalizePartyToken(a) === normalizePartyToken(b)) return true;
  return slugifyPartyKey(a) === slugifyPartyKey(b);
}

/** Whether a feed scope value is one of the viewer's linked identities. */
export function feedPartyMatchesViewer(
  dimension: string,
  raw: string,
  ctx: FeedViewerPartyContext,
): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;

  if (dimension === "sales_person") {
    const link = ctx.salesPersonLink?.trim();
    return link ? partyTokensEqual(trimmed, link) : false;
  }

  if (dimension === "opened_by" || dimension === "operator") {
    return (ctx.openedByValues ?? []).some((v) => partyTokensEqual(trimmed, v));
  }

  if (dimension === "agent") {
    return (ctx.agentValues ?? []).some((v) => partyTokensEqual(trimmed, v));
  }

  return false;
}

export function formatFeedPartyLabel(
  dimension: string,
  raw: string,
  ctx?: FeedViewerPartyContext | null,
): string {
  const effective = ctx ?? liveFeedViewerParty() ?? EMPTY_CTX;
  if (hasFeedViewerPartyLink(effective) && feedPartyMatchesViewer(dimension, raw, effective)) {
    return "You";
  }
  const property =
    dimension === "operator" ? "opened_by" : dimension;
  return formatDimensionValue(property, raw);
}

/** Portfolio owners tagged on a feed row (entity scope + scoped payload). */
export function feedEventScopedParties(event: {
  entity_scope?: FeedEntityScope | null;
  payload?: unknown;
}): Array<{ dimension: string; raw: string }> {
  const parties: Array<{ dimension: string; raw: string }> = [];
  const push = (dimension: string, raw: string | null | undefined) => {
    const trimmed = raw?.trim();
    if (!trimmed) return;
    parties.push({ dimension, raw: trimmed });
  };

  const scope = event.entity_scope ?? {};
  push("sales_person", scope.sales_person);
  push("opened_by", scope.opened_by);
  push("agent", scope.agent);

  const payload = event.payload;
  if (!payload || typeof payload !== "object") return parties;

  const record = payload as Record<string, unknown>;
  const nestedScope = record.scope;
  if (
    nestedScope &&
    typeof nestedScope === "object" &&
    "dimension" in nestedScope &&
    "key" in nestedScope
  ) {
    const dim = String((nestedScope as { dimension: string }).dimension);
    const key = String((nestedScope as { key: string }).key);
    if (dim && dim !== "org") {
      const mapped =
        dim === "operator" ? "opened_by" : dim === "sales_person" ? "sales_person" : dim;
      push(mapped, key);
    }
  }

  if (typeof record.scope === "string" && record.scope !== "org") {
    const dim = record.scope === "operator" ? "opened_by" : record.scope;
    push(dim, typeof record.dimension_key === "string" ? record.dimension_key : null);
  }

  return parties;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Replace a portfolio owner's display name with You / you in feed copy. */
export function replaceSelfPartyNameInFeedCopy(text: string, name: string): string {
  if (!name.trim()) return text;
  const esc = escapeRegExp(name.trim());
  let out = text;
  out = out.replace(new RegExp(`( · )${esc}(?=\\.|$)`, "i"), "$1You");
  out = out.replace(new RegExp(`( for )${esc}\\.`, "i"), "$1you.");
  out = out.replace(new RegExp(`( for )${esc}$`, "i"), "$1you");
  return out;
}

/** Rewrite stored API title/summary when the scoped owner is the viewer. */
export function personalizeFeedEventText(
  text: string | null | undefined,
  event: { entity_scope?: FeedEntityScope | null; payload?: unknown },
  ctx?: FeedViewerPartyContext | null,
): string {
  if (!text) return text ?? "";
  const effective = ctx ?? liveFeedViewerParty();
  if (!effective || !hasFeedViewerPartyLink(effective)) return text;

  let out = text;
  for (const party of feedEventScopedParties(event)) {
    if (!feedPartyMatchesViewer(party.dimension, party.raw, effective)) continue;
    const formatted = formatPersonName(party.raw);
    const names = new Set<string>();
    if (formatted && formatted !== "—") names.add(formatted);
    names.add(party.raw.trim());
    for (const name of names) {
      out = replaceSelfPartyNameInFeedCopy(out, name);
    }
  }
  return out;
}

/** Keep feed copy helpers in sync outside React (same pattern as viewer scope). */
export function useSyncFeedViewerParty() {
  const { profile } = useUser();
  const ctx = useMemo(
    () =>
      resolveFeedViewerPartyContext({
        salesPersonLink: profile?.sales_person_link,
        openedByValues: profile?.opened_by_values,
        agentValues: profile?.agent_values,
      }),
    [
      profile?.sales_person_link,
      profile?.opened_by_values,
      profile?.agent_values,
    ],
  );

  setLiveFeedViewerParty(ctx);
  useLayoutEffect(() => {
    setLiveFeedViewerParty(ctx);
    return () => setLiveFeedViewerParty(null);
  }, [ctx]);
}
